import React, { useEffect } from "react";
import Context from "../../../../../../context";

import { useFormik } from "formik";
import {
  ICreateAndUpdateDriver,
  IPlateNumber,
} from "../../../../../../types/driver.interface";
import {
  CustomButton2,
  CustomInput,
  CustomTimePiker,
  CustomView,
  PlateNumber,
} from "../../../../../../components";

import * as Yup from "yup";
import TitleLine from "../../../../../../components/TitleLine";
import CustomDropDownSearch from "../../../../../../components/CustomDropDownSearch";
import values from "../../../../../../values";
import { useTranslation } from "react-i18next";

type Props = {
  defaultValues: any;
  onSubmit: (event: ICreateAndUpdateDriver) => void;
  onBack: (event: ICreateAndUpdateDriver) => void;
};

const CarInformation = (props: Props) => {
  const { isLoading } = React.useContext(Context.DriverScreen);
  const [expireDate, setExpireDate] = React.useState<string>("");
  const [plateNumber, setPlateNumber] = React.useState<
    IPlateNumber | undefined
  >({});
  const [carModelState, setCarModelState] = React.useState<{
    name: string;
    value: number;
  }>({ name: "1400", value: 1400 });
  const [dropDownErrors, setDropDownErrors] = React.useState<any>({
    carModel: "",
  });
  const { t } = useTranslation();

  useEffect(() => {
    const carModel = props?.defaultValues?.driverInformation?.carModel;
    setCarModelState({ name: carModel, value: carModel?.toString() });
    setPlateNumber(props?.defaultValues?.driverInformation?.plateNumber);
    setExpireDate(props?.defaultValues?.driverInformation?.insuranceExpiryDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ?? ===========================================================================================================
  // ??                                           Use Formik Function
  // ?? -----------------------------------------------------------------------------------------------------------
  const formik = useFormik<ICreateAndUpdateDriver>({
    enableReinitialize: true,
    initialValues: {
      ...props?.defaultValues?.driverInformation,
    },
    validationSchema: Yup.object({
      carBrand: Yup.string().required("این فیلد اجباری است"),
      carColor: Yup.string().required("این فیلد اجباری است"),
      carSystem: Yup.string().required("این فیلد اجباری است"),
      insuranceNumber: Yup.string().required("این فیلد اجباری است"),
      vin: Yup.string().required("این فیلد اجباری است"),
      driverLicenseNumber: Yup.string().required("این فیلد اجباری است"),
      uniqueCodeThirdPartyInsurance: Yup.string().required(
        "این فیلد اجباری است"
      ),
    }),
    onSubmit: (values: ICreateAndUpdateDriver, { setSubmitting }) => {
      const formValues: ICreateAndUpdateDriver = {
        ...values,
        plateNumber,
        insuranceExpiryDate: expireDate,
        carModel: carModelState.name,
      };
      console.log(formValues);

      if (
        carModelState?.name &&
        expireDate &&
        plateNumber?.iran &&
        plateNumber.letter &&
        plateNumber.threeDigit &&
        plateNumber.twoDigit
      ) {
        Promise.resolve(props.onSubmit(formValues)).catch((e) =>
          setSubmitting(false)
        );
      }
    },
  });
  const submitHandler = () => {
    let errors = {};
    if (!carModelState) {
      errors = {
        ...errors,
        carModel: t("select_car_produce_year_is_required"),
      };
    }
    if (!expireDate) {
      errors = {
        ...errors,
        expireDate: t("select_insurance_expire_is_required"),
      };
    }

    if (
      !plateNumber?.iran ||
      !plateNumber.letter ||
      !plateNumber.threeDigit ||
      !plateNumber.twoDigit
    ) {
      errors = { ...errors, plateNumber: t("plate_number_is_required") };
    }
    setDropDownErrors(errors);

    formik.submitForm();
  };

  // ?? ===========================================================================================================
  // ??                                              JSX Content
  // ?? -----------------------------------------------------------------------------------------------------------

  return (
    <form onSubmit={submitHandler}>
      <CustomView.Container className="update-form-content">
        <TitleLine title={t("carInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="carBrand"
              id="carBrand"
              label={t("carBrand")}
              value={formik.values.carBrand}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.carBrand && formik.touched.carBrand}
              errorText={formik.errors.carBrand}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomDropDownSearch
              itemName="name"
              items={values.dropdownItems.CAR_MODAL}
              placeHolder={t("carProduce")}
              onSelectItem={(event) => {
                setCarModelState(event);
                setDropDownErrors({ ...dropDownErrors, carModel: "" });
              }}
              defaultItem={carModelState}
              label={t("carProduce")}
            />
            <div className="error">
              {dropDownErrors.carModel !== "" && dropDownErrors.carModel}
            </div>
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="carColor"
              id="carColor"
              label={t("carColor")}
              value={formik.values.carColor}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.carColor && formik.touched.carColor}
              errorText={formik.errors.carColor}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="carSystem"
              id="carSystem"
              label={t("carSystem")}
              value={formik.values.carSystem}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.carSystem && formik.touched.carSystem}
              errorText={formik.errors.carSystem}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="insuranceNumber"
              id="insuranceNumber"
              label={t("insuranceNumber")}
              value={formik.values.insuranceNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={
                formik.errors.insuranceNumber && formik.touched.insuranceNumber
              }
              errorText={formik.errors.insuranceNumber}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("insuranceExpire")}</label>
            <CustomTimePiker
              defaultValue={expireDate}
              onChange={(event: string) => {
                setExpireDate(event);
                setDropDownErrors({ ...dropDownErrors, expireDate: "" });
              }}
            />
            <div className="error">
              {dropDownErrors.expireDate !== "" && dropDownErrors.expireDate}
            </div>
          </CustomView.Column>
        </CustomView.Row>

        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="vin"
              id="vin"
              label={t("vin")}
              value={formik.values.vin}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.vin && formik.touched.vin}
              errorText={formik.errors.vin}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="driverLicenseNumber"
              id="driverLicenseNumber"
              label={t("driverLicenseNumber")}
              value={formik.values.driverLicenseNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={
                formik.errors.driverLicenseNumber &&
                formik.touched.driverLicenseNumber
              }
              errorText={formik.errors.driverLicenseNumber}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="uniqueCodeThirdPartyInsurance"
              id="uniqueCodeThirdPartyInsurance"
              label={t("insuranceUniqueCode")}
              value={formik.values.uniqueCodeThirdPartyInsurance}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={
                formik.errors.uniqueCodeThirdPartyInsurance &&
                formik.touched.uniqueCodeThirdPartyInsurance
              }
              errorText={formik.errors.uniqueCodeThirdPartyInsurance}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("plateNumber")}</label>
            <PlateNumber
              editor
              onChange={(event: IPlateNumber) => {
                setPlateNumber(event);
                setDropDownErrors({ ...dropDownErrors, plateNumber: "" });
              }}
              tagNum={
                plateNumber
                  ? plateNumber
                  : props?.defaultValues?.driverInformation?.plateNumber
              }
            />
            <div className="error">
              {dropDownErrors.plateNumber !== "" && dropDownErrors.plateNumber}
            </div>
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={() => props.onBack({ ...formik.values, plateNumber })}
              title={t("back")}
              type="danger"
            />
          </CustomView.Column>
          <CustomView.Column
            large={3}
            medium={4}
            small={6}
            className="form-colum"
          >
            <CustomButton2
              isLoading={isLoading}
              onClick={submitHandler}
              title={t("next")}
            />
          </CustomView.Column>
        </CustomView.Row>
      </CustomView.Container>
    </form>
  );
};

export default CarInformation;
